import { NavLink, Outlet, useMatch } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { IntegrationBanner } from "@/components/IntegrationBanner";
import { EzyVetQuotaProvider } from "@/context/EzyVetQuotaContext";
import { useTenant } from "@/hooks/useTenant";
import { useLiveEvents } from "@/hooks/useLiveEvents";
import { ToastContainer } from "@/components/Toast";
import { Logo } from "@/components/Logo";
import { useAuthStore } from "@/store/auth";
import { ThemePicker } from "@/components/ThemePicker";

export function Layout() {
  const clear = useAuthStore((s) => s.clear);
  const userId = useAuthStore((s) => s.userId);
  const clinicId = useAuthStore((s) => s.clinicId);
  const qc = useQueryClient();
  const { pmsType, integrationStatus, isEzyVet } = useTenant();
  const wide = useMatch("/reconciliation/*");
  const live = useLiveEvents();

  // Cached queries belong to this user's clinic; drop them so the next sign-in starts clean
  const signOut = () => {
    clear();
    qc.clear();
  };

  return (
    <div className="app">
      <aside className="sidebar">
        <Logo variant="sidebar" />
        <NavLink to="/" end className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
          Dashboard
        </NavLink>
        <NavLink to="/audit" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
          Run Audit
        </NavLink>
        <NavLink to="/appointments" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
          Appointments
        </NavLink>
        <NavLink to="/leakage" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
          Leakage Summary
        </NavLink>

        <div className="nav-section-title">Integrations</div>
        <NavLink to="/settings/integrations/ezyvet" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
          ezyVet
        </NavLink>
        <NavLink to="/settings/integrations/cornerstone" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
          Cornerstone
        </NavLink>

        <div className="footer">
          <div style={{ marginBottom: "var(--s-1)" }}>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", marginBottom: 4 }}>Theme</div>
            <ThemePicker />
          </div>
          <div className={`live-status ${live}`} title="New audits appear without reloading while this says Live">
            <span className="live-dot" aria-hidden="true" />
            {live === "live" ? "Live" : live === "connecting" ? "Connecting…" : "Offline, retrying"}
          </div>
          {userId && <div className="mono">{userId}</div>}
          {clinicId && <div className="mono" title={clinicId}>clinic: {clinicId.slice(0, 8)}…</div>}
          <button className="btn btn-sm" onClick={signOut}>
            Sign out
          </button>
        </div>
      </aside>
      <main className={wide ? "main main--wide" : "main"}>
        <IntegrationBanner pmsType={pmsType} status={integrationStatus} />
        <EzyVetQuotaProvider enabled={isEzyVet}>
          <Outlet />
        </EzyVetQuotaProvider>
        <ToastContainer />
      </main>
    </div>
  );
}
